import React, { useEffect, useState, useCallback } from "react";
import { useNotifications } from "../contexts/NotificationContext";
import { useTheme } from "../contexts/ThemeSelection";
import { InAppNotification } from "../types";
import styles from "../styles/NotificationToast.module.css";

const TOAST_DURATION = 5000;

const typeIcons: Record<InAppNotification["type"], string> = {
  overdue: "\u26a0\ufe0f",
  today: "\ud83d\udcc5",
  reminder: "\u23f0",
  info: "\u2139\ufe0f",
  nudge: "\ud83d\udca1",
  streak: "\ud83d\udd25",
  announcement: "\ud83d\udce3",
  social: "\ud83d\udc65",
};

const NotificationToast: React.FC = () => {
  const { notifications } = useNotifications();
  const { activeTheme, theme } = useTheme();
  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
  const [hidingIds, setHidingIds] = useState<Set<string>>(new Set());
  const [seenIds, setSeenIds] = useState<Set<string>>(new Set());

  const dismiss = useCallback(
    (id: string) => {
      setHidingIds((prev) => new Set(prev).add(id));
      setTimeout(() => {
        setVisibleIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setHidingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        // Don't mark as read — toast dismissal is visual only.
        // Notifications stay unread until viewed in the bell dropdown.
      }, 300);
    },
    []
  );

  // Show new unread notifications as toasts
  useEffect(() => {
    for (const n of notifications) {
      if (!n.read && !seenIds.has(n.id)) {
        setSeenIds((prev) => new Set(prev).add(n.id));
        setVisibleIds((prev) => new Set(prev).add(n.id));
      }
    }
  }, [notifications, seenIds]);

  // Auto-dismiss after duration
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    visibleIds.forEach((id) => {
      if (!hidingIds.has(id)) {
        const timer = setTimeout(() => dismiss(id), TOAST_DURATION);
        timers.push(timer);
      }
    });
    return () => timers.forEach(clearTimeout);
  }, [visibleIds, hidingIds, dismiss]);

  const visibleToasts = notifications.filter((n) => visibleIds.has(n.id));

  if (visibleToasts.length === 0) return null;

  return (
    <div className={styles.toastContainer}>
      {visibleToasts.slice(0, 3).map((n) => (
        <div
          key={n.id}
          className={`${styles.toast} ${hidingIds.has(n.id) ? styles.hiding : ""}`}
          style={{
            backgroundColor: theme[activeTheme].panelColor,
            color: theme[activeTheme].color,
            border: theme[activeTheme].border,
          }}
          onClick={() => dismiss(n.id)}
        >
          <span className={styles.toastIcon}>{typeIcons[n.type]}</span>
          <div className={styles.toastContent}>
            <div className={styles.toastTitle}>{n.title}</div>
            <div className={styles.toastBody}>{n.body}</div>
          </div>
          <button
            className={styles.toastClose}
            style={{ color: theme[activeTheme].color }}
            onClick={(e) => {
              e.stopPropagation();
              dismiss(n.id);
            }}
          >
            &times;
          </button>
        </div>
      ))}
    </div>
  );
};

export default NotificationToast;
